import { trpc } from '@client-trpc';
import type {
  TemplateConfigInput,
  TemplateType
} from '@server/trading-templates/schemas/input.schema';

type MutationOptions = {
  invalidateTradingTemplates?: boolean;
  invalidateDefaultTemplates?: boolean;
};

const defaultOptions: Required<MutationOptions> = {
  invalidateTradingTemplates: true,
  invalidateDefaultTemplates: false
};

const resolveOptions = (
  options?: MutationOptions
): Required<MutationOptions> => ({
  ...defaultOptions,
  ...options
});

export const useTemplateMutations = () => {
  const utils = trpc.useUtils();

  const createTemplateMutation =
    trpc.tradingTemplates.createTradingTemplate.useMutation();
  const updateTemplateMutation =
    trpc.tradingTemplates.updateTradingTemplate.useMutation();
  const deleteTemplatesMutation =
    trpc.tradingTemplates.deleteTradingTemplates.useMutation();
  const setDefaultMutation = trpc.tradingTemplates.setTemplateAsDefault.useMutation();
  const clearDefaultMutation = trpc.tradingTemplates.clearTemplateDefault.useMutation();

  const invalidateQueries = async (
    templateType?: TemplateType,
    options?: MutationOptions
  ) => {
    const resolvedOptions = resolveOptions(options);

    if (resolvedOptions.invalidateTradingTemplates) {
      if (templateType) {
        await utils.tradingTemplates.tradingTemplates.invalidate({ templateType });
      } else {
        await utils.tradingTemplates.tradingTemplates.invalidate();
      }
    }

    if (resolvedOptions.invalidateDefaultTemplates) {
      await utils.tradingTemplates.defaultTemplates.invalidate();
    }
  };

  const createTemplate = async (
    input: { name: string; isBundle: boolean; config: TemplateConfigInput },
    options?: MutationOptions
  ) => {
    const templateId = await createTemplateMutation.mutateAsync(input);
    await invalidateQueries(input.config.type, options);
    return templateId;
  };

  const updateTemplate = async (
    input: { templateId: string; name: string; config: TemplateConfigInput },
    options?: MutationOptions
  ) => {
    const result = await updateTemplateMutation.mutateAsync(input);
    await invalidateQueries(input.config.type, options);
    return result;
  };

  const deleteTemplates = async (
    input: { templateIds: string[] },
    options?: MutationOptions
  ) => {
    const result = await deleteTemplatesMutation.mutateAsync(input);
    await invalidateQueries(undefined, {
      invalidateTradingTemplates: true,
      invalidateDefaultTemplates: true,
      ...options
    });
    return result;
  };

  const toggleDefaultTemplate = async (
    input: { templateType: TemplateType; templateId: string; isDefault: boolean },
    options?: MutationOptions
  ) => {
    const result = input.isDefault
      ? await clearDefaultMutation.mutateAsync({
          templateType: input.templateType,
          templateId: input.templateId
        })
      : await setDefaultMutation.mutateAsync({
          templateType: input.templateType,
          templateId: input.templateId
        });

    await invalidateQueries(input.templateType, {
      invalidateTradingTemplates: true,
      invalidateDefaultTemplates: true,
      ...options
    });
    return result;
  };

  return {
    createTemplate,
    updateTemplate,
    deleteTemplates,
    toggleDefaultTemplate,
    isPending:
      createTemplateMutation.isPending ||
      updateTemplateMutation.isPending ||
      deleteTemplatesMutation.isPending ||
      setDefaultMutation.isPending ||
      clearDefaultMutation.isPending
  };
};
